import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/Button';
import { authService } from '../../services';
import { setTokens, setUserData } from '../../utils/auth';
import routes from '../../utils/routes';
import './VerifyOTP.css';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const otpInputs = useRef([]);

  // Get email and context from location state
  const email = location.state?.email;
  const isLogin = location.state?.isLogin;
  const isPasswordReset = location.state?.isPasswordReset;

  // Redirect if no email is provided
  useEffect(() => {
    if (!email) {
      navigate(routes.public.LOGIN);
    }
  }, [email, navigate]);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Handle input change
  const handleChange = (e, index) => {
    const { value } = e.target;
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    setOtp((prevOtp) => {
      const newOtp = [...prevOtp];
      newOtp[index] = value.slice(0, 1); // Only take the first digit
      return newOtp;
    });

    // Auto-focus next input
    if (value.length === 1 && index < 5) {
      otpInputs.current[index + 1].focus();
    }
  };

  // Handle backspace key
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input when backspace on empty input
      otpInputs.current[index - 1].focus();
    }
  };

  // Handle pasting OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedOtp = pastedData.slice(0, 6).split('');
    
    if (/^\d+$/.test(pastedData)) {
      setOtp((prevOtp) => {
        const newOtp = [...prevOtp];
        pastedOtp.forEach((digit, index) => {
          if (index < 6) {
            newOtp[index] = digit;
          }
        });
        return newOtp;
      });
      
      // Focus last input or the next empty one
      const focusIndex = Math.min(5, pastedOtp.length);
      otpInputs.current[focusIndex].focus();
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpValue = otp.join('');
    
    if (otpValue.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      // Log the OTP details
      console.log('OTP entered:', {
        email,
        context: isLogin ? 'login' : isPasswordReset ? 'password-reset' : 'registration',
        otpValue
      });
      
      // For password reset, DON'T call any verification API - just collect the OTP
      if (isPasswordReset) {
        console.log('Password reset OTP collected, redirecting to reset password page', {
          email,
          resetCode: otpValue
        });
        
        // Store the data for the reset password page
        const resetPasswordState = { 
          resetCode: otpValue,
          email: email
        };
        
        // Store the state in session storage as a backup
        sessionStorage.setItem('resetPasswordState', JSON.stringify(resetPasswordState));
        
        // Navigate to reset password page
        navigate(routes.public.RESET_PASSWORD, { 
          state: resetPasswordState,
          replace: true
        });
        setIsLoading(false);
        return;
      }
      
      // For other contexts (login, registration), proceed with verification API call
      let response;
      try {
        // For login/registration contexts, use the email verification endpoint
        response = await authService.verifyEmail(email, otpValue);
        console.log('OTP Verification response:', response.data);
        
        if (isLogin) {
          // If login context, save tokens and redirect to dashboard
          const { accessToken, refreshToken, user } = response.data;
          setTokens(accessToken, refreshToken);
          setUserData(user);
          navigate(routes.protected.DASHBOARD);
        } else {
          // If registration context, check for autoLogin property or assume default behavior
          if (response.data.accessToken && response.data.refreshToken) {
            // We have tokens, so auto-login
            const { accessToken, refreshToken, user } = response.data;
            setTokens(accessToken, refreshToken);
            setUserData(user || { email });
            navigate(routes.protected.DASHBOARD);
          } else {
            // No tokens, redirect to login
            navigate(routes.public.LOGIN, {
              state: { message: 'Account verified successfully. You can now log in.' }
            });
          }
        }
      } catch (apiError) {
        console.error('API Error during OTP verification:', apiError);
        
        if (apiError.response) {
          console.error('Response data:', apiError.response.data);
          console.error('Response status:', apiError.response.status);
          
          const errorMessage = apiError.response.data?.message || 
                              apiError.response.data?.error ||
                              'Invalid verification code. Please try again.';
          setError(errorMessage);
        } else {
          setError('An error occurred during verification. Please try again.');
        }
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError(
        error.response?.data?.message || 
        'Invalid OTP. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP resend
  const handleResendOTP = async () => {
    if (!canResend) return;
    
    try {
      setCanResend(false);
      setTimer(60);
      
      if (isPasswordReset) {
        await authService.forgotPassword(email);
      } else if (isLogin) {
        // Re-trigger login with OTP
        // Note: Your API might have a specific endpoint for this
        await authService.forgotPassword(email);
      } else {
        // Re-trigger registration OTP
        // Note: Your API might have a specific endpoint for this
        await authService.forgotPassword(email);
      }
      
      // Show success message
      setError('');
      // You could set a success message here if needed
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(
        error.response?.data?.message || 
        'Failed to resend OTP. Please try again.'
      );
    }
  };

  return (
    <div className="otp-container">
      <div className="otp-card">
        <div className="otp-header">
          <h1 className="otp-title">Verification</h1>
          <p className="otp-subtitle">
            We've sent a 6-digit code to {email}
          </p>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: 'var(--spacing-md)' }}>
            {error}
          </div>
        )}

        <form className="otp-form" onSubmit={handleSubmit}>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                value={digit}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : null}
                className="otp-input"
                maxLength={1}
                ref={(el) => (otpInputs.current[index] = el)}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <div className="otp-timer">
            {timer > 0 ? (
              <span>Resend code in {timer} seconds</span>
            ) : (
              <span>You can now resend the code</span>
            )}
          </div>

          <div className="otp-resend">
            <button 
              type="button" 
              className="otp-resend-button"
              onClick={handleResendOTP}
              disabled={!canResend}
            >
              Resend Code
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="otp-submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTP; 